import { useQuery } from '@apollo/client/react'
import { dedupe } from '@/helpers/dedupe'
import { Table as BaseTable, type TableProps } from '../table'
import { usePageInfo } from '@/hooks/usePageInfo'
import { Slimline as Error } from '@/components/error'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { graphql } from '@/gql'
import type { TestExecutionFilters, TestExecutionOrder } from '@/gql/graphql'
import { prettyDateTime } from '@/helpers/text_formatters'
import { ResultBadge } from './result_badge'

const TestExecutionsQuery = graphql(`
  query TestExecutions($filters: TestExecutionFilters, $order: TestExecutionOrder, $after: String) {
    testExecutions(filters: $filters, order: $order, after: $after) {
      nodes {
        id
        finishedAt
        equipment { id barcode serialNumber }
        item { id name }
        pass
      }
      count
      pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
    }
  }
`)

interface Props extends TableProps {
  filters?: TestExecutionFilters
}

export const Table = ({ filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery(TestExecutionsQuery, { variables: { filters } })

  const { pageInfo } = usePageInfo(data?.testExecutions?.pageInfo)

  if (loading) return <LoadingSkeleton table />

  return (
    <>
      { error && <Error error={ error } /> }
      { data && <BaseTable<TestExecutionFilters, TestExecutionOrder>
        { ...props }
        initialFilters={ filters }
        moreToFetch={ pageInfo.current?.hasNextPage }
        refetch={ refetch }
        totalRows={ data.testExecutions.count }
        fetchMore={ () => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.testExecutions.nodes = dedupe([...previousData.testExecutions.nodes, ...fetchMoreResult.testExecutions.nodes])
            return { ...fetchMoreResult }
          }
        }) }
        filters={ [
          {
            index: 1,
            type: 'text',
            placeholder: 'Start typing...',
            filterKey: 'nameCont',
          },
          {
            index: 0,
            type: 'text',
            placeholder: 'Start typing...',
            filterKey: 'barcodeCont',
          },
          {
            index: 3,
            type: 'select',
            filterKey: 'passEq',
            options: [
              { value: true, display: <ResultBadge pass={true}/>},
              { value: false, display: <ResultBadge pass={false}/> },
            ]
          },
        ] }
        headings={ [
          { name: 'Barcode (Serial Number)' },
          { name: 'Name' },
          { name: 'Date' },
          { name: 'Result' },
          { name: 'Sync Status' },
        ] }
        rows={ data?.testExecutions?.nodes.map(testExecution => ({
          id: testExecution.id,
          columns: [
            <span>{ testExecution.equipment.barcode } { testExecution.equipment.serialNumber && (testExecution.equipment.serialNumber) }</span>,
            testExecution.item.name,
            testExecution.finishedAt ? prettyDateTime(new Date(testExecution.finishedAt as string)) : <span></span>,
            testExecution.pass && <ResultBadge pass={testExecution.pass}/>,
            <span></span>,
          ],
          link: `/tests/${testExecution.id}`,
        })) }
      /> }
    </>
  )
}
